import React, { useState } from "react";
import { ENDPOINTS, WEB_URL } from "@/config/endpoints";
import Link from "next/link";
import dynamic from "next/dynamic";
import { FormProvider } from "@/component/shared/contactForm/formContext";
import Head from "next/head";
import { getHTSCodeSchema } from "@/lib/htsCodeSchema";
import { getHTSCodeBreadcrumbSchema } from "@/lib/htsCodesBreadcrumbSchema";

const MexicoImportSearch = dynamic(
  () => import("@/component/shared/mexicoImport-search/mexicoImportSearch"),
  { loading: () => <p>Loading...</p> }
);
const ContactFormStep1 = dynamic(
  () => import("@/component/shared/contactForm/contactFormStep1"),
  {
    loading: () => <p>Loading...</p>,
  }
);
const ContactFormStep2 = dynamic(
  () => import("@/component/shared/contactForm/contactFormStep2"),
  {
    loading: () => <p>Loading...</p>,
  }
);

const Importduty = ({ id, chaptersData, relatedHTS }) => {
  const [showNextStep, setShowNextStep] = useState(false);
  const showSteps = () => {
    setShowNextStep(true);
  };
  const showFirstStep = () => {
    setShowNextStep(false);
  };

  function textCapitalized(value) {
    if (value != null) {
      const loweredCase = value.toLowerCase();
      return value[0].toUpperCase() + loweredCase.slice(1);
    }
  }

  const htsCodeBreadcrumbSchema = getHTSCodeBreadcrumbSchema({
    chapterTitle: `Chapter ${chaptersData.chapter}`,
    chapterPageUrl:
      WEB_URL + `/mexico-import-duty/chapter/${chaptersData.chapter}`,
    hsnCode: chaptersData.code,
    hsnCodePageUrl: WEB_URL + `/mexico-import-duty/${String(chaptersData.code).replace(/\./g, "")}`,
    page: "mexico-import-duty",
  });

  const htsCodeSchema = getHTSCodeSchema({
    pageMetaTitle:
      "Mexico Import Duty Rates for HS Code " +
      JSON.stringify(chaptersData.code).replace(/^"(.*)"$/, "$1") +
      ": " +
      chaptersData.description,
    pageUrl: WEB_URL + `/mexico-import-duty/${String(chaptersData.code).replace(/\./g, "")}`,
    pageMetaDesc:
      "Check Mexico import duty and IVA tax for HS Code " +
      chaptersData.code +
      ". Get tariff rates for " +
      chaptersData.description,
    hsnCode: chaptersData.code,
    hsnCodeDesc: chaptersData.description,
    page: "mexico-import-duty",
  });

  const formatDate = (dateStr) => {
    const d = new Date(dateStr);
    return d.toLocaleDateString("en-GB");
  };
  
  return (
    <main className="mt-sp-100 sm:mt-20">
      <MexicoImportSearch />

      <Head>
        <title>{`Mexico Import Duty Rate for HS Code ${chaptersData.code} - ${chaptersData.description}`}</title>
        <meta
          name="description"
          content={`Check Mexico import duty and IVA tax for HS Code ${chaptersData.code}. Get tariff rates for ${chaptersData.description}`}
        />
        {/* OpenGraph tags */}
        <meta
          property="og:title"
          content={
            "Mexico Import Duty Rate for HS Code " +
            JSON.stringify(chaptersData.code).replace(/^"(.*)"$/, "$1") +
            ": " +
            chaptersData.description
          }
        />
        <meta
          property="og:description"
          content={`Check Mexico import duty and IVA tax for HS Code ${chaptersData.code}. Get tariff rates for ${chaptersData.description}`}
        />
        <meta
          property="og:url"
          content={
            WEB_URL + "/mexico-import-duty/" + String(chaptersData.code).replace(/\./g, "")
          }
        />
        <link
          rel="canonical"
          href={
            WEB_URL + "/mexico-import-duty/" + String(chaptersData.code).replace(/\./g, "")
          }
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{
            __html: JSON.stringify(htsCodeBreadcrumbSchema),
          }}
        />
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(htsCodeSchema) }}
        />
      </Head>

      <section className="bg-white py-20">
        <div className="container px-3 xm:px-2 lg:px-3">
          <div className="flex flex-wrap gap-3">
            <div className="w-[60%] sm:w-full pr-4 sm:pr-0 md:pr-0">
              <div className="mb-5">
                <p className="text-sm text-secondary mb-2">
                  <Link href={WEB_URL} rel="noopener noreferrer">
                    <span className="text-[#2369d8] cursor-pointer font-sm">
                      {" "}
                      Home {"  "}
                    </span>
                  </Link>
                  {"  "}
                  {" > "}
                  <Link
                    href={WEB_URL + `/mexico-import-duty`}
                    rel="noopener noreferrer"
                  >
                    <span className="text-[#2369d8] cursor-pointer font-sm">
                      {" "}
                      Mexico Import Duty {"  "}
                    </span>
                  </Link>
                  {">"}
                  <Link
                    href={
                      WEB_URL +
                      `/mexico-import-duty/chapter/${chaptersData.chapter}`
                    }
                    rel="noopener noreferrer"
                  >
                    <span className="text-[#2369d8] cursor-pointer font-sm">
                      {"  "}
                      {"Chapter"} {chaptersData.chapter}{" "}
                    </span>
                  </Link>{" "}
                  {id.length > 4 && (
                    <>
                      {" > "}
                      <Link
                        href={WEB_URL + `/mexico-import-duty/${id.slice(0, 4)}`}
                        rel="noopener noreferrer"
                      >
                        <span className="text-[#2369d8] cursor-pointer font-sm">
                          {"  "}
                          {id.slice(0, 4)}{" "}
                        </span>
                      </Link>
                    </>
                  )}{" "}
                  {" > "} {chaptersData.code}
                </p>
                <h1 className="text-base text-secondary">
                  {`Mexico Import Duty Rate for HS Code ${chaptersData.code} : ${chaptersData.description}`}
                </h1>
              </div>
              <div className="flex items-center justify-center bg-white">
                <div className="bg-[#F9FBFF] rounded-2xl shadow-sm border border-borderColor p-8 sm:p-2 w-full">
                  <h2 className="text-[#2369d8] text-2xl sm:text-base2 font-bold mb-4">
                    HS Code: {chaptersData.code}
                  </h2>
                  <h3 className="text-lg font-medium text-gray-900 mb-4 sm:text-base2">
                    {chaptersData.description
                      ? textCapitalized(chaptersData.description)
                      : "N/A"}
                  </h3>
                  <div className="flex flex-wrap text-sm text-gray-700">
                    <div className="flex flex-col w-1/2 gap-4">
                      <div>
                        <p className="text-gray-500 text-base">Unit</p>
                        <p className="font-medium text-gray-900 text-base2">
                          {chaptersData.unit ? chaptersData.unit : "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-base">Export Tax</p>
                        <p className="font-medium text-gray-900 text-base2">
                          {chaptersData.export_tax_rate
                            ? chaptersData.export_tax_rate
                            : "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-base">Expiry Date</p>
                        <p className="font-medium text-gray-900 text-base2">
                          {chaptersData.expiry_date
                            ? formatDate(chaptersData.expiry_date)
                            : "N/A"}
                        </p>
                      </div>
                    </div>
                    <div className="flex flex-col w-1/2 gap-4">
                      <div>
                        <p className="text-gray-500 text-base">Import Duty</p>
                        <p className="font-medium text-gray-900 text-base2">
                          {chaptersData.import_tax_rate
                            ? chaptersData.import_tax_rate
                            : "N/A"}
                        </p>
                      </div>
                      <div>
                        <p className="text-gray-500 text-base">
                          Effective Date
                        </p>
                        <p className="font-medium text-gray-900 text-base2">
                          {chaptersData.effective_date
                            ? formatDate(chaptersData.effective_date)
                            : "N/A"}
                        </p>
                      </div>
                      {/* <div>
                        <p className="text-gray-500 text-base">
                         Chapter
                        </p>
                        <p className="font-medium text-gray-900 text-base2">
                          {chaptersData.effective_date ? formatDate(chaptersData.effective_date) : "N/A"}
                        </p>
                      </div> */}
                    </div>
                  </div>

                  {chaptersData.immex_ban && (
                    <div className="flex items-center gap-2 mt-6 bg-[#FFE5E5] text-[#D8000C] text-sm sm:text-xs font-medium px-3 py-2 sm:py-1 rounded-2xl w-fit">
                      <span>🚫</span>
                      <p>{chaptersData.ban_description}</p>
                    </div>
                  )}
                  {chaptersData.deleted_item_Annex_II && (
                    <div className="flex items-center gap-2 mt-6 bg-gradient-to-r from-yellow-100 to-yellow-50 border border-yellow-200 text-black text-sm sm:text-xs font-medium px-3 py-1 rounded-2xl w-fit">
                      <span>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          width="24"
                          height="24"
                          fill="currentColor"
                        >
                          <path d="M4 8H20V21C20 21.5523 19.5523 22 19 22H5C4.44772 22 4 21.5523 4 21V8ZM7 5V3C7 2.44772 7.44772 2 8 2H16C16.5523 2 17 2.44772 17 3V5H22V7H2V5H7ZM9 4V5H15V4H9ZM9 12V18H11V12H9ZM13 12V18H15V12H13Z"></path>
                        </svg>
                      </span>
                      <p>{chaptersData.deleted_item_description}</p>
                    </div>
                  )}
                </div>
              </div>

              <div className="w-full pt-6">
                {relatedHTS.length > 1 && (
                  <>
                    <h3 className="font-medium text-lg mb-3">
                      More HS codes from Chapter {chaptersData.chapter}
                    </h3>
                    <ul className="flex flex-wrap">
                      {relatedHTS.map(
                        (data) =>
                          data.code && (
                            <li
                              key={data.code}
                              className="first:pl-0 relative text-sm  after:content-['|'] after:px-2 last:after:content-[''] after:text-sm mb-2"
                            >
                              <Link
                                className="text-secondary hover:text-yellowcolor hover:underline"
                                href={
                                  WEB_URL +
                                  `/mexico-import-duty/${String(
                                    data.code
                                  ).replace(/\./g, "")}`
                                }
                              >
                                HS Code {data.code}
                              </Link>
                            </li>
                          )
                      )}
                    </ul>
                  </>
                )}
              </div>
            </div>
            <div className="w-[38%] sm:w-full">
              <div className="md:mt-5 lg:top-[130px] xm:top-[100px]">
                <div className="w-full flex flex-col gap-4">
                  {/* Decree Information */}
                  <div className="bg-[#EAF3FD] p-3 rounded-lg border border-borderColor">
                    <h3 className="text-blue-700 font-semibold text-xl mb-2 flex items-center">
                      <span className="mr-2">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 24 24"
                          width="24"
                          height="24"
                          fill="#022B5C"
                        >
                          <path d="M14.0049 20.0028V22.0028H2.00488V20.0028H14.0049ZM14.5907 0.689087L22.3688 8.46726L20.9546 9.88147L19.894 9.52792L17.4191 12.0028L23.076 17.6597L21.6617 19.0739L16.0049 13.417L13.6007 15.8212L13.8836 16.9525L12.4693 18.3668L4.69117 10.5886L6.10539 9.17437L7.23676 9.45721L13.53 3.16396L13.1765 2.1033L14.5907 0.689087Z"></path>
                        </svg>
                      </span>{" "}
                      Decree Information
                    </h3>
                    <p className="text-base text-gray-700">
                      <span className="font-semibold">Title:</span> DECREE
                      amending the tariff of the Law on General Import and
                      Export Taxes
                    </p>
                    <p className="text-base text-gray-700 mt-1 mb-xsp">
                      <span className="font-semibold">Publication Date:</span>{" "}
                      19 December 2024
                    </p>
                    <p className="text-base text-gray-700 mb-xsp">
                      <span className="font-semibold">Effective Date:</span> 20
                      December 2024
                    </p>
                    <p className="text-base text-gray-700 mb-xsp">
                      <span className="font-semibold">Expiry Date:</span> 23
                      April 2026
                    </p>
                    <p className="text-base text-gray-700">
                      <span className="font-semibold">Issuing Authority:</span>{" "}
                      CLAUDIA SHEINBAUM PARDO
                    </p>
                  </div>

                  {/* Finance Box */}
                  <div className="bg-gradient-to-r from-yellow-100 to-yellow-50 p-4 rounded-lg border border-yellow-200 text-center">
                    <h3 className="font-semibold text-gray-800 mb-1 text-xl">
                      Looking for Finance?
                    </h3>
                    <p className="text-base text-gray-600 mb-3">
                      Explore Import Factoring solutions with Credlix
                    </p>
                    <Link
                      href="/import-factoring"
                      className="group flex items-center py-1 px-5 rounded-md w-fit text-white text-base mx-auto bg-gradient-to-r from-[#022B5C] to-[#0363C4] shadow-lg transition-all duration-300 hover:from-[#0363C4] hover:to-[#022B5C] gap-1"
                    >
                      Explore Import factoring
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        width="24"
                        height="24"
                        className="transition-transform duration-300 transform group-hover:translate-x-2 fill-current text-white"
                      >
                        <path d="M16.1716 10.9999L10.8076 5.63589L12.2218 4.22168L20 11.9999L12.2218 19.778L10.8076 18.3638L16.1716 12.9999H4V10.9999H16.1716Z" />
                      </svg>
                    </Link>
                  </div>
                </div>
                <FormProvider>
                  {!showNextStep ? (
                    <ContactFormStep1 showStep1={showSteps} />
                  ) : (
                    <ContactFormStep2
                      showPrevStep={showFirstStep}
                      formType="GetInTouchNew"
                    />
                  )}
                </FormProvider>
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Importduty;

export async function getServerSideProps(context) {
  const { res } = context;
  const heading = context.params.importDuty;
  let relatedHTS = [];
  let chaptersData = {};
  let filteredRelatedHTS = [];

  const formatHeadingNumber = (num) => {
    if (!num) return "";
    const str = num.toString().padStart(8, "0");
    return `${str.slice(0, 4)}.${str.slice(4, 6)}.${str.slice(6, 8)}`;
  };

  const locales = context.locales || ['en-in', 'en-us', 'es-mx'];
  if (!heading || heading === "null" || heading === "undefined" || heading === "[importDuty]" || locales.includes(heading)) {
    return { notFound: true };
  }

  let options = {
    headers: {
      "Content-Type": "application/json",
    },
  };

  try {
    const formatted = formatHeadingNumber(heading);
    const apiRes = await fetch(
      `${ENDPOINTS.IMPORT_DUTY_API}hts-code?code=${formatted}`,
      options
    );

    if (apiRes.status === 500) {
      console.error("Server error 500 from API");
      res.statusCode = 500;
      return { notFound: true };
    }

    chaptersData = await apiRes.json();

    const relatedMatches =
      chaptersData.chapter?.trim() !== "" ||
      chaptersData.description?.trim() !== "";

    if (!relatedMatches) {
      return { notFound: true };
    }

    const relatedApiRes = await fetch(
      `${ENDPOINTS.IMPORT_DUTY_API}chapter/hts-codes?chapter=${chaptersData[0].chapter}`,
      options
    );

    if (relatedApiRes.status === 500) {
      console.error("Server error 500 from related HTS codes API");
      res.statusCode = 500;
      return { notFound: true };
    }
    relatedHTS = await relatedApiRes.json();

    filteredRelatedHTS = relatedHTS.filter((item) => item.code !== formatted);
  } catch (err) {
    console.error("Error fetching chapter data:", err);
    res.statusCode = 500;
    return { notFound: true };
  }

  return {
    props: {
      id: chaptersData[0].chapter,
      chaptersData: chaptersData[0] || {},
      relatedHTS: filteredRelatedHTS || [],
    },
  };
}
